import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Activity, CheckCircle2, Waves, Smartphone, ChevronRight, Apple } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { db, auth, setDocWithQueue } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import { fetchWrapper, safeJsonResponse } from '../lib/api';

interface HealthSyncConfigProps {
  lang: string;
  userId?: string;
  theme?: string;
  onSyncNow?: () => Promise<void>;
  isSyncing?: boolean;
}

export function HealthSyncConfig({ lang, userId, theme, onSyncNow, isSyncing }: HealthSyncConfigProps) {
  const [syncStatus, setSyncStatus] = useState<'IDLE' | 'PENDING' | 'AUTHORIZED'>('IDLE');
  const [isExpanded, setIsExpanded] = useState(false);
  
  const [appleStatus, setAppleStatus] = useState<'IDLE' | 'PENDING' | 'AUTHORIZED'>('IDLE');
  const [isAppleExpanded, setIsAppleExpanded] = useState(false);
  const [isAppleSyncing, setIsAppleSyncing] = useState(false);
  const [simulatedCalories, setSimulatedCalories] = useState<number>(450);



  useEffect(() => {
    if (!userId) return;
    const fetchStatus = async () => {
      try {
        const cached = (window as any).__userDocCache;
        if (cached && cached.uid === userId && cached.data) {
          if (cached.data.googleFitConnected) {
            setSyncStatus('AUTHORIZED');
          } else {
            const localConnected = localStorage.getItem('google_fit_connected') === 'true';
            if (localConnected) setSyncStatus('AUTHORIZED');
            else setSyncStatus('IDLE');
          }
          
          if (cached.data.appleHealthConnected) {
            setAppleStatus('AUTHORIZED');
          } else {
            const localApple = localStorage.getItem('apple_health_connected') === 'true';
            if (localApple) setAppleStatus('AUTHORIZED');
            else setAppleStatus('IDLE');
          }
          return;
        }

        const userDoc = await getDoc(doc(db, 'users', userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.googleFitConnected) {
            setSyncStatus('AUTHORIZED');
          } else {
            const localConnected = localStorage.getItem('google_fit_connected') === 'true';
            if (localConnected) setSyncStatus('AUTHORIZED');
            else setSyncStatus('IDLE');
          }
          
          if (data.appleHealthConnected) {
            setAppleStatus('AUTHORIZED');
          } else {
            const localApple = localStorage.getItem('apple_health_connected') === 'true';
            if (localApple) setAppleStatus('AUTHORIZED');
            else setAppleStatus('IDLE');
          }
        } else {
          setSyncStatus('IDLE');
          setAppleStatus('IDLE');
        }
      } catch (err) {
        console.warn("Failed to fetch connection status:", err);
      }
    };
    
    fetchStatus();

    const handleConnected = () => {
      console.log("[HealthSync] Google Fit connected event received");
      setSyncStatus('AUTHORIZED');
    };
    const handleDisconnected = () => {
      setSyncStatus('IDLE');
    };

    window.addEventListener('google-fit-connected', handleConnected);
    window.addEventListener('google-fit-disconnected', handleDisconnected);
    
    return () => {
      window.removeEventListener('google-fit-connected', handleConnected);
      window.removeEventListener('google-fit-disconnected', handleDisconnected);
    };
  }, [userId]);

  const handleSyncRequest = async () => {
    if (syncStatus === 'AUTHORIZED') {
      // Optimistic disconnect
      localStorage.removeItem('google_fit_connected');
      localStorage.removeItem('google_fit_access_token');
      localStorage.removeItem('google_fit_token_timestamp');
      setSyncStatus('IDLE');
      toast.success(lang === 'ua' ? 'Синхронізацію відключено' : 'Health Sync Disconnected');
      window.dispatchEvent(new CustomEvent('google-fit-disconnected'));

      if (userId) {
        setDocWithQueue(doc(db, 'users', userId), {
          googleFitConnected: false,
          googleFitAccessToken: null,
          googleFitTokenTimestamp: null
        }, { merge: true }).catch(err => {
          console.error("Failed to disconnect Google Fit in background:", err);
        });
      }
      return;
    }

    setSyncStatus('PENDING');
    localStorage.setItem('google_fit_pending_link', 'true');
    
    const provider = new GoogleAuthProvider();
    // Google Fit Scopes for nutrition, hydration, and activity
    provider.addScope('https://www.googleapis.com/auth/fitness.nutrition.read');
    provider.addScope('https://www.googleapis.com/auth/fitness.nutrition.write');
    provider.addScope('https://www.googleapis.com/auth/fitness.activity.read');
    provider.addScope('https://www.googleapis.com/auth/fitness.activity.write');
    provider.setCustomParameters({ 
      prompt: 'consent',
      access_type: 'offline' 
    }); // force consent screen so user selects scopes and request refresh token capability

    const handleRedirectFlow = async () => {
      try {
        // Detect if we are in an iframe (AI Studio preview)
        const inIframe = window.self !== window.top;
        if (inIframe) {
          console.warn("[HealthSync] Redirect requested in iframe, blocking...");
          toast.error(
            lang === 'ua' 
              ? 'Авторизація через редирект неможлива у вікні попереднього перегляду. Будь ласка, відкрийте додаток у новій вкладці або дозвольте спливаючі вікна.' 
              : 'Redirect authorization is not supported in the preview frame. Please open the app in a new tab or allow popups.',
            { duration: 6000 }
          );
          localStorage.removeItem('google_fit_pending_link');
          setSyncStatus('IDLE');
          return;
        }

        console.log("[HealthSync] Initiating signInWithRedirect for Google Fit scopes...");
        await signInWithRedirect(auth, provider);
      } catch (redirectErr) {
        console.error("[HealthSync] Redirect flow initiation failed:", redirectErr);
        localStorage.removeItem('google_fit_pending_link');
        setSyncStatus('IDLE');
        toast.error(lang === 'ua' ? 'Помилка запуску авторизації' : 'Failed to launch authorization');
      }
    };

    if (window.innerWidth < 768) {
      handleRedirectFlow();
      return;
    }

    try {
      const popupPromise = signInWithPopup(auth, provider);
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 12000)
      );
      const result = await Promise.race([popupPromise, timeoutPromise]);
      localStorage.removeItem('google_fit_pending_link');
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const token = credential?.accessToken;

      if (!token) {
        throw new Error("No Google Fit API access token returned");
      }

      // Optimistic connect
      localStorage.setItem('google_fit_connected', 'true');
      localStorage.setItem('google_fit_access_token', token);
      localStorage.setItem('google_fit_token_timestamp', String(Date.now()));
      setSyncStatus('AUTHORIZED');
      toast.success(
        lang === 'ua' 
          ? 'Успішно підключено до Google Fit!' 
          : 'Successfully linked to Google Fit!'
      );
      window.dispatchEvent(new CustomEvent('google-fit-connected'));

      if (userId) {
        setDocWithQueue(doc(db, 'users', userId), {
          googleFitConnected: true,
          googleFitAccessToken: token,
          googleFitTokenTimestamp: Date.now()
        }, { merge: true }).catch(err => {
          console.error("Failed to update Google Fit token in background:", err);
        });
      }
    } catch (err: any) {
      console.error("Failed to link Google Fit via popup, trying redirect fallback:", err);
      if (err?.code === 'auth/popup-blocked' || err?.code === 'auth/popup-closed-by-user') {
        toast.info(lang === 'ua' ? 'Перенаправлення на сторінку авторизації...' : 'Redirecting to authorization page...');
        handleRedirectFlow();
      } else {
        localStorage.removeItem('google_fit_pending_link');
        setSyncStatus('IDLE');
        toast.error(
          lang === 'ua' 
            ? 'Помилка підключення Google Fit. Перевірте з\'єднання.' 
            : 'Failed to connect Google Fit. Please try again.'
        );
      }
    }
  };

  const handleAppleSyncRequest = async () => {
    if (appleStatus === 'AUTHORIZED') {
      // Optimistic disconnect
      localStorage.removeItem('apple_health_connected');
      setAppleStatus('IDLE');
      window.dispatchEvent(new CustomEvent('apple-health-disconnected'));
      toast.success(lang === 'ua' ? 'Apple Health відключено' : 'Apple Health Disconnected');

      if (userId) {
        setDocWithQueue(doc(db, 'users', userId), {
          appleHealthConnected: false,
          appleHealthTodayCalories: null,
          appleHealthLastSynced: null
        }, { merge: true }).catch(err => {
          console.error("Failed to disconnect Apple Health in background:", err);
        });
      }
      return;
    }

    // Optimistic connect
    localStorage.setItem('apple_health_connected', 'true');
    setAppleStatus('AUTHORIZED');
    window.dispatchEvent(new CustomEvent('apple-health-connected'));
    toast.success(lang === 'ua' ? 'Apple Health успішно підключено!' : 'Successfully linked Apple Health!');

    if (userId) {
      setDocWithQueue(doc(db, 'users', userId), {
        appleHealthConnected: true
      }, { merge: true }).catch(err => {
        console.error("Failed to connect Apple Health in background:", err);
      });
    }
  };

  const handleAppleSyncNow = async () => {
    try {
      setIsAppleSyncing(true);

      const response = await fetchWrapper('/api/health-sync/sync-daily', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source: 'apple-health',
          calories: simulatedCalories
        })
      });

      if (!response.ok) {
        throw new Error("Apple Health sync returned non-OK response");
      }

      const resData = await safeJsonResponse(response);
      toast.success(
        lang === 'ua' 
          ? `Apple Health синхронізовано! Сьогодні: ${resData.todayCalories} ккал` 
          : `Apple Health synced! Today is ${resData.todayCalories} kcal`
      );
    } catch (error) {
      console.error(error);
      toast.error(lang === 'ua' ? 'Помилка синхронізації з Apple Health' : 'Failed to sync Apple Health');
    } finally {
      setIsAppleSyncing(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
      {/* Google Fit Card */}
      <div className={cn(
        "border rounded-2xl overflow-hidden",
        theme === 'light' ? "bg-white border-zinc-200 shadow-sm" : "bg-zinc-900 border-zinc-800"
      )}>
        <div 
          className="p-5 flex items-center justify-between cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Activity className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-500">
                Google Fit
              </h3>
              <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mt-0.5">
                {lang === 'ua' ? 'Незабаром' : 'Coming soon'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {syncStatus === 'AUTHORIZED' && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-bold uppercase tracking-wider">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {lang === 'ua' ? 'Підключено' : 'Linked'}
              </span>
            )}
            <ChevronRight className={cn(
              "w-5 h-5 text-zinc-400 transition-transform duration-300",
              isExpanded ? "rotate-90" : ""
            )} />
          </div>
        </div>

        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="px-5 pb-5 pt-2 border-t border-black/5 dark:border-white/5 overflow-hidden"
            >
            <p className="text-zinc-500 text-sm mb-6 leading-relaxed">
              {lang === 'ua' 
                ? 'Автоматично синхронізуйте ваші макроси та воду в фоновому режимі (без затримок UI) для пристроїв Android. Записуйте їжу через фото чи чат, і ми надішлемо метрики прямо у ваш акаунт Google Fit та екосистему Health Connect.'
                : 'Securely sync your macros and water metrics in the background (zero UI freeze) on Android devices. Log your meals via photo or chat, and we pipe the data directly to your Google Fit account and Health Connect ecosystem.'}
            </p>

            <div className="bg-white dark:bg-zinc-800 p-4 rounded-xl border border-[var(--card-border)] flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center shrink-0">
                <Smartphone className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-[var(--text)] uppercase tracking-wide mb-1">
                  Google Fit & Health Connect (Android)
                </h4>
                <p className="text-xs text-zinc-500 leading-relaxed mb-3">
                  {lang === 'ua'
                    ? 'Двонаправлена синхронізація харчування, гідратації та спалених калорій безпосередньо з вашим профілем Android.'
                    : 'Bi-directional sync of nutritional intake, hydration metrics, and active calories burned directly with your Android profile.'}
                </p>
                <div className="text-xs text-zinc-500 flex flex-wrap gap-1.5">
                  <span className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-700 font-mono text-[10px]">nutrition.write</span>
                  <span className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-700 font-mono text-[10px]">hydration.write</span>
                  <span className="px-2 py-0.5 rounded bg-zinc-100 dark:bg-zinc-700 font-mono text-[10px]">activity.read</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              {syncStatus === 'AUTHORIZED' && onSyncNow && (
                <button
                  onClick={() => onSyncNow()}
                  disabled={isSyncing}
                  className={cn(
                    "px-6 py-2.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-all",
                    "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20",
                    isSyncing && "opacity-70 cursor-not-allowed"
                  )}
                >
                  {isSyncing ? (
                    lang === 'ua' ? 'СИНХРОНІЗАЦІЯ...' : 'SYNCING...'
                  ) : (
                    lang === 'ua' ? 'СИНХРОНІЗУВАТИ' : 'SYNC NOW'
                  )}
                </button>
              )}
              <button
                onClick={handleSyncRequest}
                disabled={syncStatus === 'PENDING'}
                className={cn(
                  "px-6 py-2.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-all",
                  syncStatus === 'AUTHORIZED'
                    ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                    : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20",
                  syncStatus === 'PENDING' && "opacity-70 cursor-not-allowed"
                )}
              >
                {syncStatus === 'PENDING' ? (
                  lang === 'ua' ? 'ПІДКЛЮЧЕННЯ...' : 'LINKING...'
                ) : syncStatus === 'AUTHORIZED' ? (
                  lang === 'ua' ? 'ВІДКЛЮЧИТИ' : 'DISCONNECT'
                ) : (
                  lang === 'ua' ? 'АВТОРИЗУВАТИСЯ' : 'AUTHORIZE SYNC'
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>

      {/* Apple Health Card */}
      <div className={cn(
        "border rounded-2xl overflow-hidden",
        theme === 'light' ? "bg-white border-zinc-200 shadow-sm" : "bg-zinc-900 border-zinc-800"
      )}>
        <div 
          className="p-5 flex items-center justify-between cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          onClick={() => setIsAppleExpanded(!isAppleExpanded)}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <Apple className="w-6 h-6 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-500">
                Apple Health
              </h3>
              <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest mt-0.5">
                {lang === 'ua' ? 'Незабаром' : 'Coming soon'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {appleStatus === 'AUTHORIZED' && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-bold uppercase tracking-wider">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {lang === 'ua' ? 'Підключено' : 'Linked'}
              </span>
            )}
            <ChevronRight className={cn(
              "w-5 h-5 text-zinc-400 transition-transform duration-300",
              isAppleExpanded ? "rotate-90" : ""
            )} />
          </div>
        </div>

        <AnimatePresence initial={false}>
          {isAppleExpanded && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="px-5 pb-5 pt-2 border-t border-black/5 dark:border-white/5 overflow-hidden"
            >
            <p className="text-zinc-500 text-sm mb-6 leading-relaxed">
              {lang === 'ua' 
                ? 'Синхронізуйте ваші калорії, макронутрієнти та споживання води безпосередньо з додатком Apple Health (HealthKit). Запишіть їжу в MainNutrition, і метрики автоматично відобразяться на вашому iPhone.'
                : 'Sync your calories, macronutrients, and water logs directly with Apple Health app (HealthKit). Record your meals in MainNutrition, and watch the parameters populate cleanly on your iPhone.'}
            </p>

            {appleStatus === 'AUTHORIZED' && (
              <div className="bg-white dark:bg-zinc-800 p-4 rounded-xl border border-[var(--card-border)] mb-6">
                <h4 className="font-bold text-sm text-[var(--text)] uppercase tracking-wide mb-2">
                  {lang === 'ua' ? 'Симулятор калорій Apple Health' : 'Apple Health Calorie Simulator'}
                </h4>
                <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                  {lang === 'ua'
                    ? 'Виберіть кількість калорій, зареєстровану у вашому Apple Health сьогодні, для синхронізації з хмарою Firestore.'
                    : 'Select the active/dietary calories currently logged inside your Apple Health database today to sync back with our Cloud Firestore.'}
                </p>
                <div className="flex items-center gap-4">
                  <input 
                    type="range" 
                    min="0" 
                    max="2000" 
                    step="50" 
                    value={simulatedCalories} 
                    onChange={(e) => setSimulatedCalories(Number(e.target.value))}
                    className="w-full accent-emerald-500 h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                  />
                  <span className="font-mono font-bold text-sm text-[var(--text)] shrink-0 min-w-[70px] text-right">
                    {simulatedCalories} kcal
                  </span>
                </div>
                <div className="flex gap-2 mt-3">
                  {[300, 600, 1000, 1500].map((val) => (
                    <button
                      key={val}
                      onClick={() => setSimulatedCalories(val)}
                      type="button"
                      className={cn(
                        "px-2.5 py-1 rounded text-xs font-semibold font-mono border transition-all",
                        simulatedCalories === val
                          ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                          : "bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-600 hover:bg-zinc-200 dark:hover:bg-zinc-600"
                      )}
                    >
                      {val} kcal
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3">
              {appleStatus === 'AUTHORIZED' && (
                <button
                  onClick={handleAppleSyncNow}
                  disabled={isAppleSyncing}
                  className={cn(
                    "px-6 py-2.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-all",
                    "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20",
                    isAppleSyncing && "opacity-70 cursor-not-allowed"
                  )}
                >
                  {isAppleSyncing ? (
                    lang === 'ua' ? 'СИНХРОНІЗАЦІЯ...' : 'SYNCING...'
                  ) : (
                    lang === 'ua' ? 'СИНХРОНІЗУВАТИ' : 'SYNC NOW'
                  )}
                </button>
              )}
              <button
                onClick={handleAppleSyncRequest}
                disabled={appleStatus === 'PENDING'}
                className={cn(
                  "px-6 py-2.5 rounded-xl font-bold text-sm uppercase tracking-wider transition-all",
                  appleStatus === 'AUTHORIZED'
                    ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                    : "bg-emerald-500 text-white hover:bg-emerald-600 shadow-lg shadow-emerald-500/20",
                  appleStatus === 'PENDING' && "opacity-70 cursor-not-allowed"
                )}
              >
                {appleStatus === 'PENDING' ? (
                  lang === 'ua' ? 'ПІДКЛЮЧЕННЯ...' : 'LINKING...'
                ) : appleStatus === 'AUTHORIZED' ? (
                  lang === 'ua' ? 'ВІДКЛЮЧИТИ' : 'DISCONNECT'
                ) : (
                  lang === 'ua' ? 'АВТОРИЗУВАТИСЯ' : 'AUTHORIZE SYNC'
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}
